/* ----------------------------------------------------------------------------
 * listenGuard — bounded-timeout policy for the push-to-talk LISTENING phase.
 *
 * P0 (2026-07-20, owner real-device + desktop): after pressing Talk the UI could
 * stay on "Listening"/"Sending…" indefinitely. The push-to-talk machine only
 * LEAVES 'listening' when the controller dispatches STT_FINAL / ERROR / STOP, so
 * ANY stall in the record→stop→STT path (a missed pointer-release that never
 * stops recording, or a transcribe round-trip that never returns) leaves the
 * user stuck on "Listening" with no recovery.
 *
 * This module is the pure, browser-free, node-testable policy the controller
 * uses to guarantee the listening phase ALWAYS terminates:
 *   - RECORD CAP: a hard ceiling on the record window, so a missed release can
 *     never record forever — on expiry we auto-SEND what was captured.
 *   - FINALIZE GUARD: a ceiling on the STT round-trip after release, so a stalled
 *     transcribe can never hang "Sending…" — on expiry we RECOVER (abort + a
 *     recoverable error the next press clears).
 *
 * PTT-ONLY as of Phase A (voice-turn-audio-redesign, 2026-07-27): the RECORD
 * CAP (LISTEN_RECORD_CAP_MS) used to be reused for hands-free wake/clarify
 * capture too (it arms on ANY transition into VoiceStatus==listening, which
 * both PTT and wake share via the same startListening()) -- that was the
 * governor race: a status-only timer with zero knowledge of the wake
 * CommandEndpointer could outrace its verdicts. The caller
 * (useVoiceController.ts record-cap effect) now skips arming this cap while
 * handsFreeTurnRef is true; a hands-free capture is owned exclusively by
 * CaptureWindow (app/src/voice/wake/captureWindow.ts), which has its own
 * single clock + single terminal event. This module logic is unchanged --
 * only the call site now scopes it to PTT.
 * NEITHER re-arms listening (that was the regression risk): each is single-shot
 * and moves the machine OUT of 'listening'. Pure + deterministic -> `node --test`.
 * -------------------------------------------------------------------------- */

import type { VoiceStatus, VoiceError } from './types.ts';

/** Hard ceiling (ms) on the RECORD window (finger down, before release). Long
 *  enough for a normal spoken utterance; a missed release auto-sends at the cap
 *  instead of recording forever. */
export const LISTEN_RECORD_CAP_MS = 20000;

/** Ceiling (ms) on the FINALIZE window (release -> transcript). A stalled STT
 *  POST (or a MediaRecorder that never fires `stop`) is recovered here instead
 *  of hanging on "Sending…". Comfortably exceeds a normal transcribe round-trip. */
export const STT_FINALIZE_TIMEOUT_MS = 12000;

/** What a fired listening-phase timer should do, decided from the CURRENT phase:
 *   - 'send'    — still recording (a release was missed): auto-submit the audio.
 *   - 'recover' — release happened but the STT round-trip stalled: abort + error.
 *   - 'noop'    — the machine already left 'listening' (or nothing to send): do
 *                 nothing (prevents any re-arm / double-handling race). */
export type ListenTimeoutAction = 'send' | 'recover' | 'noop';

/** Pure decision for a fired guard. `hasStream` reflects whether an STT capture
 *  stream is still open (something to send). Emptiness/phase only — no phrase or
 *  length heuristics. */
export function listenTimeoutAction(
  status: VoiceStatus,
  finalizing: boolean,
  hasStream: boolean,
): ListenTimeoutAction {
  if (status !== 'listening') return 'noop'; // already progressed — never re-arm
  if (finalizing) return 'recover';          // released; STT in flight but stalled
  return hasStream ? 'send' : 'noop';        // still recording -> auto-submit
}

/** The recoverable error surfaced when the finalize guard fires (STT stalled).
 *  Phrase-agnostic; the reducer clears it on the next press so the user can retry. */
export const STT_TIMEOUT_ERROR: VoiceError = {
  code: 'stt_failed',
  message: 'That took too long. Please try again.',
};
